import React, { useRef, useState, useEffect } from 'react';
import { Text, Button } from 'ui';
import { useAuth, updatePage, usePage, refreshPagePreview } from 'api';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import AceEditor from "react-ace";
import pretty from 'pretty';
import cssbeautify from 'cssbeautify';

import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/theme-github";
import "./index.css";

export default function PageCode(props) {
  
  const auth = useAuth();
  const frameRef = useRef();
  const history = useHistory();
  const companyId = auth ? auth.company.id : null;
  const {pageId} = useParams();
  const page = usePage(companyId, pageId);
  
  
  // Toolbar and Layout Settings
  
  const [breakpoint, setBreakpoint] = useState('desktop');
  const [file, setFile] = useState('html');
  
  
  // Handle resizing the code view. 
  
  const [codeWidth, setCodeWidth] = useState(400);
  const [resizing, setResizing] = useState(false);
  
  
  
  // Create local copy of page HTML and CSS. When the page update, 
  // update the local copy.
  
  const [html, setHTML] = useState("");
  const [css, setCSS] = useState("");
  
  useEffect(() => {
    if(!page) {
      return;
    }
    
    setHTML(pretty(page.html || ""));
    setCSS(cssbeautify(page.css || ""));
  }, [page]);
  
  
  // When the code updates, update the preview.
  
  useEffect(() => {
    
    const code = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <title>Taptivate</title>
          <style>
            html {
              width: 100%;
              height: 100%;
              padding: 0;
              margin: 0;
            }
            
            body {
              width: 100%;
              min-height: 100%;
              padding: 0;
              margin: 0;
              background: white;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
                "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
                sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            
            #root {
              width: 100%;
              min-height: 100vh;
              height: 0px;
            }
            
            ${css}
          </style>
        </head>
        <body>
          <div id="root">
            ${html}
          </div>
        </body>
      </html>
    `;
    
    const doc = frameRef.current.contentWindow.document;
    doc.open();
    doc.write(code);
    doc.close();
    
  }, [html, css]);
  
  
  
  
  // Save the page and redirect to the page view.
  
  async function save() {
    await updatePage(companyId, pageId, {html, css,});
    refreshPagePreview(companyId, pageId);
    history.push('/pages');
  }
  
  
  // Update local code.
  
  function change(value) {
    if(file === 'html') {
      setHTML(value);
    }
    else {
      setCSS(value);
    }
  }
  
  
  
  
  // Render
  
  if(pageId && pageId.type === 'design') {
    return <Redirect to="edit"/>;
  }
  
  
  return (
    <div className="pagecode-wrapper">
      <div className="pagecode-left">
        <div className="pagecode-toolbar">
          <div className="pagecode-name">
            <Text bold color="#5cc2e8">{page && page.name}</Text>
          </div>
          <div className="pagecode-tools">
            <div className={`pagecode-tool ${breakpoint === 'desktop' ? 'active' : ''}`} onClick={() => setBreakpoint('desktop')}>
              <svg width="24" height="24" viewBox="0 0 576 512"><path fill="currentColor" d="M528 0H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h192l-16 48h-72c-13.3 0-24 10.7-24 24s10.7 24 24 24h272c13.3 0 24-10.7 24-24s-10.7-24-24-24h-72l-16-48h192c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zm-16 352H64V64h448v288z"></path></svg>
            </div>
            <div className={`pagecode-tool ${breakpoint === 'mobile' ? 'active' : ''}`} onClick={() => setBreakpoint('mobile')}>
              <svg width="24" height="24" viewBox="0 0 320 512"><path fill="currentColor" d="M272 0H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM160 480c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm112-108c0 6.6-5.4 12-12 12H60c-6.6 0-12-5.4-12-12V60c0-6.6 5.4-12 12-12h200c6.6 0 12 5.4 12 12v312z"></path></svg>
            </div>
          </div>
          <div style={{flex:1}}/>
        </div>
        <iframe 
          title="preview"
          className="pagecode-frame"
          ref={frameRef}
          src="about:blank"
          style={{width:breakpoint === 'mobile' ? 320 : '100%'}}
        />
      </div>
      <div className="pagecode-right" style={{width:codeWidth}}>
        <div className="pagecode-toolbar">
          <div className="pagecode-tools">
            <div className={`pagecode-tool ${file === 'html' ? 'active' : ''}`} onClick={() => setFile('html')}>
              <Text block small bold color="inherit">HTML</Text>
            </div>
            <div className={`pagecode-tool ${file === 'css' ? 'active' : ''}`} onClick={() => setFile('css')}>
              <Text block small bold color="inherit">CSS</Text>
            </div>
          </div>
        </div>
        
        <div style={{flex:1}}>
          <AceEditor
            mode="javascript"
            theme="github"
            value={file === 'html' ? html : css}
            onChange={change}
            name="codeedit"
            width="100%"
            height="100%"
          />
        </div>
        
        
        <div className="pagecode-save">
          <Button label="SAVE & CLOSE" small white onClick={save}/>
        </div>
        
        <div 
          className="pagecode-splithandle" 
          onMouseDown={() => setResizing(true)}
        />
        
      </div>
      
      <div 
        onMouseMove={e => resizing && setCodeWidth(document.body.clientWidth - e.pageX)}
        onMouseUp={e => setResizing(false)}
        style={{
          display: resizing ? 'block' : 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
        }}
      />
    </div>
  );
}